import { useCallback, useReducer, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { attachmentErrorKey, checkAttachment, MessageUpload, storedFileName } from '../../Helpers/Agents/attachments';
import { showErrorToast, showWarningToast } from '../../Helpers/general';

export interface AttachmentItem {
  id: string;
  file: File;
}

type Action = { type: 'add'; items: AttachmentItem[] } | { type: 'remove'; id: string } | { type: 'clear' };

let lastId = 0;
const nextId = () => {
  lastId += 1;
  return `attachment-${lastId}`;
};

const reducer = (items: AttachmentItem[], action: Action): AttachmentItem[] => {
  switch (action.type) {
    case 'add':
      return [...items, ...action.items];
    case 'remove':
      return items.filter((item) => item.id !== action.id);
    case 'clear':
      return [];
    default:
      return items;
  }
};

/** The files waiting in the composer and the lifecycle of one send. A file that fails a check never joins. */
const useAttachments = () => {
  const { t } = useTranslation();
  const [items, dispatch] = useReducer(reducer, []);
  // Read by `add`, so two adds before a render still see each other's files.
  const itemsRef = useRef(items);
  itemsRef.current = items;

  const reasonFor = useCallback(
    (fileName: string | null, code: string) => {
      const reason = t(attachmentErrorKey(code));
      return fileName ? `${fileName}: ${reason}` : reason;
    },
    [t],
  );

  const add = useCallback(
    (files: FileList | File[]) => {
      const added: AttachmentItem[] = [];
      Array.from(files).forEach((file) => {
        const code = checkAttachment(
          file,
          [...itemsRef.current, ...added].map((item) => item.file),
        );
        if (code) showWarningToast(reasonFor(file.name, code));
        else added.push({ id: nextId(), file });
      });
      if (added.length === 0) return;
      itemsRef.current = [...itemsRef.current, ...added];
      dispatch({ type: 'add', items: added });
    },
    [reasonFor],
  );

  const remove = useCallback((id: string) => dispatch({ type: 'remove', id }), []);
  /** Drops the files not sent yet, e.g. when the user moves to another conversation or agent. */
  const clear = useCallback(() => dispatch({ type: 'clear' }), []);

  /**
   * Moves the files out of the composer into an upload for `sendUserMessage`.
   * `settled` resolves when the core API takes or refuses them: false gives the text back to the composer.
   */
  const startSend = useCallback((): { upload: MessageUpload; settled: Promise<boolean> } | null => {
    const files = itemsRef.current.map((item) => item.file);
    if (files.length === 0) return null;
    itemsRef.current = [];
    dispatch({ type: 'clear' });

    let settle: (keepText: boolean) => void = () => {};
    const settled = new Promise<boolean>((resolve) => {
      settle = resolve;
    });

    const upload: MessageUpload = {
      files,
      onAccepted: () => settle(true),
      onRejected: (error, isOpen) => {
        if (error) showErrorToast(reasonFor(error.fileName, error.code));
        if (!isOpen) {
          settle(true);
          return;
        }
        // The turn is gone, so the files come back for another try, except the one the core API refused.
        add(files.filter((file) => !error || storedFileName(file.name) !== error.fileName));
        settle(false);
      },
    };
    return { upload, settled };
  }, [add, reasonFor]);

  return { items, add, remove, clear, startSend, hasFiles: items.length > 0 };
};

export type Attachments = ReturnType<typeof useAttachments>;

export default useAttachments;
